import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { Form, useSubmit } from 'react-router'
import { ClearedErrorData } from '~/api/apiError'
import { ActionBar, ContinueButton } from '~/components/ActionBar'
import { ActionErrorAlert } from '~/components/ActionErrorAlert'
import { Button, ButtonLink } from '~/components/Button'
import { Input } from '~/components/Input'
import { JovivaLogo } from '~/components/JovivaLogo'
import { LoginIntentENUM } from '~/dashboard/LoginForm'
import { RequestRestPasswordDialog } from '~/dashboard/RequestRestPasswordDialog'
import { recaptchaPrecheckSubmit } from '~/dashboard/utils'
import { ROUTES } from '~/routes-typed'
import { defaultGrid, gridChildClasses } from '~/utils/layout'

const formClasses = {
  rowWrapper: 'flex flex-col gap-4 py-3',
  labelElement: 'flex flex-col gap-2',
}

const classes = {
  tabButton: 'basis-1/2 rounded-2xl p-3 outline-hidden',
  activeTabButton: 'bg-lavender-700 text-white ',
  inactiveTabButton: 'text-lavender-700',
}

const TEXT = {
  description:
    'Melde dich einfach mit deiner Smile-ID an und werde Teil von joviva!',
  buttonLabel: 'Login mit Smile-ID',
  changePasswordDialogHeader: 'Passwort zurücksetzen',
  changePasswordDialogDescription:
    'Wir werden dir eine E-Mail schicken mit einer Anleitung, um ein neues Passwort einzugeben.',
  tabsLabel: {
    smileId: 'Smile ID Login',
    emailPassword: 'E-Mail & Passwort',
  },
  forgetPassword: 'Passwort vergessen?',
  email: 'E-Mail',
  password: 'Passwort',
  login: 'Anmelden',
  logout: 'Mit anderer Smile-ID anmelden',
}

export interface LoginFormProps {
  connectionError?: ClearedErrorData
  showConfirmDialog?: boolean
}

export const LoginForm = ({
  connectionError,
  showConfirmDialog,
}: LoginFormProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showChangePassDialog, setShowChangePassDialog] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submit = useSubmit()

  useEffect(() => {
    if (showConfirmDialog) setShowAlert(true)
  }, [showConfirmDialog])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    setIsSubmitting(true)

    const captchaToken = await recaptchaPrecheckSubmit(LoginIntentENUM.LOGIN)
    if (!captchaToken) {
      setIsSubmitting(false)
      return
    }

    formData.set('_captcha', captchaToken)
    await submit(formData, { method: 'post' })
    setIsSubmitting(false)
  }

  return (
    <div className={defaultGrid}>
      <div className={gridChildClasses.narrow4}>
        <JovivaLogo className='m-auto my-16 w-96 max-w-full' />
        <div className={'shadow-button rounded-2xl'}>
          <TabGroup
            selectedIndex={selectedIndex}
            onChange={setSelectedIndex}
            defaultIndex={0}
          >
            <TabList
              className={
                'bg-lavender-50 flex justify-between gap-2 rounded-tl-2xl rounded-tr-2xl p-4'
              }
            >
              <Tab
                className={classNames(
                  classes.tabButton,
                  selectedIndex === 0
                    ? classes.activeTabButton
                    : classes.inactiveTabButton,
                )}
              >
                <p>{TEXT.tabsLabel.smileId}</p>
              </Tab>
              <Tab
                className={classNames(
                  classes.tabButton,
                  selectedIndex === 1
                    ? classes.activeTabButton
                    : classes.inactiveTabButton,
                )}
              >
                <p>{TEXT.tabsLabel.emailPassword}</p>
              </Tab>
            </TabList>
            <TabPanels className={'p-4'}>
              <TabPanel>
                <p
                  className={classNames(
                    gridChildClasses.narrow4,
                    'text-body2 mt-4',
                  )}
                >
                  {TEXT.description}
                </p>
                {connectionError && (
                  <div className='mt-8'>
                    <ActionErrorAlert data={connectionError}>
                      <ButtonLink
                        variant='ghost'
                        to={ROUTES.API.AUTH.LOGOUT.$buildPath({})}
                        className='self-start'
                      >
                        {TEXT.logout}
                      </ButtonLink>
                    </ActionErrorAlert>
                  </div>
                )}
                <div className={classNames('text-body1 mt-10 flex sm:mt-8')}>
                  <ButtonLink
                    className='w-full'
                    to={ROUTES.API.AUTH.LOGIN.$buildPath({})}
                  >
                    {TEXT.buttonLabel}
                  </ButtonLink>
                </div>
              </TabPanel>
              <TabPanel>
                <Form
                  method='post'
                  className={formClasses.rowWrapper}
                  onSubmit={handleSubmit}
                >
                  <label className={formClasses.labelElement}>
                    <span className='text-grey-800 text-body2'>
                      {TEXT.email}
                    </span>
                    <Input
                      name='email'
                      type='email'
                      required
                      placeholder='E-Mail'
                    />
                  </label>
                  <label className={formClasses.labelElement}>
                    <span className='text-grey-800 text-body2'>
                      {TEXT.password}
                    </span>
                    <Input
                      name='password'
                      type='password'
                      required
                      placeholder='Passwort'
                    />
                  </label>
                  <div className={'mb-3'}>
                    <Button
                      className={
                        'mt-4 inline-flex rounded-none! border-b-2 border-b-black bg-transparent! px-0! pb-2 shadow-none!'
                      }
                      onClick={() => setShowChangePassDialog(true)}
                    >
                      {TEXT.forgetPassword}
                    </Button>
                  </div>
                  <div className={'mt-5'}>
                    <ActionBar intent={LoginIntentENUM.LOGIN}>
                      <ContinueButton disabled={isSubmitting}>
                        {TEXT.login}
                      </ContinueButton>
                    </ActionBar>
                  </div>
                </Form>
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
        <RequestRestPasswordDialog
          textContent={{
            headline: TEXT.changePasswordDialogHeader,
            description: TEXT.changePasswordDialogDescription,
          }}
          onClose={() => setShowChangePassDialog(false)}
          isOpen={showChangePassDialog}
          isSuccess={showAlert}
        />
      </div>
    </div>
  )
}
